var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { OperationRead } from '@galigeo-store/galigeojs';
import '../button/IconButton';
import '../icon/Icon.js';
import './StatusCard';
const BADGE_ICON_NAME = {
    add: 'plus',
    edit: 'edit',
    delete: 'close',
    transfer: 'swap',
};
const TYPE_BADGE_COLOR = () => ({
    add: { bg: '#16a34a', variant: 'success', label: msg('Add', { id: 'scenarioOperation.opType.add', desc: 'Badge label for an add operation in a scenario operation row' }) },
    edit: { bg: '#0078d4', variant: 'primary', label: msg('Edit', { id: 'scenarioOperation.opType.edit', desc: 'Badge label for an edit operation in a scenario operation row' }) },
    delete: { bg: '#dc3545', variant: 'danger', label: msg('Delete', { id: 'scenarioOperation.opType.delete', desc: 'Badge label for a delete operation in a scenario operation row' }) },
    transfer: { bg: '#f59e0b', variant: 'warning', label: msg('Transfer', { id: 'scenarioOperation.opType.transfer', desc: 'Badge label for a transfer operation in a scenario operation row' }) }
});
const FALLBACK_BADGE = () => ({
    bg: '#6b7280',
    variant: 'secondary',
    label: msg('Operation', { id: 'scenarioOperation.opType.fallback', desc: 'Fallback badge label for an unknown operation type in a scenario operation row' })
});
function metaFor(type) {
    if (!type)
        return FALLBACK_BADGE();
    return TYPE_BADGE_COLOR()[String(type).toLowerCase()] ?? FALLBACK_BADGE();
}
/**
 * Compact row for an STG operation. Renders:
 *   [type icon] [POI name] [status-card] [icon-button delete]
 *
 * The type icon (leftmost) is a colored circle with the operation symbol:
 *   - add    : green circle, white "+"
 *   - delete : red circle, white "×"
 *   - edit   : blue circle, white pencil
 *
 * Events:
 *   - `scenario-operation-click`  — body click, detail = the original operation
 *   - `scenario-operation-delete` — trash click, detail = the original operation
 *   - `scenario-operation-edit`   — edit click, detail = the original operation
 */
let ScenarioOperation = class ScenarioOperation extends LitElement {
    constructor() {
        super(...arguments);
        this.poiName = null;
        this.subtitle = null;
        this.selected = false;
        this.hideDelete = false;
        this.hideEdit = false;
        this.hideTypeBadge = false;
        this._onClick = (e) => {
            // Don't fire when the click started on the trash icon-button.
            const path = e.composedPath();
            if (path.some((n) => n.tagName === 'ICON-BUTTON'))
                return;
            this.dispatchEvent(new CustomEvent('scenario-operation-click', {
                detail: this.operation,
                bubbles: true,
                composed: true
            }));
        };
        this._onDelete = () => {
            this.dispatchEvent(new CustomEvent('scenario-operation-delete', {
                detail: this.operation,
                bubbles: true,
                composed: true
            }));
        };
        this._onEdit = () => {
            this.dispatchEvent(new CustomEvent('scenario-operation-edit', {
                detail: this.operation,
                bubbles: true,
                composed: true
            }));
        };
    }
    _name() {
        return (this.poiName?.trim() ||
            this.operation?.poi_operation?.name?.trim() ||
            '—');
    }
    _renderTypeIcon(type) {
        const key = String(type).toLowerCase();
        const bg = metaFor(type).bg;
        const iconName = BADGE_ICON_NAME[key] ?? 'edit';
        return html `<span class="type-icon" style="background:${bg}; color:#ffffff">
      <basic-icon .name=${iconName} size="14"></basic-icon>
    </span>`;
    }
    render() {
        if (!this.operation)
            return nothing;
        const meta = metaFor(this.operation.type);
        const t = String(this.operation.type).toLowerCase();
        const showEdit = !this.hideEdit && (t === 'add' || t === 'edit' || t === 'transfer');
        const showActions = showEdit || !this.hideDelete;
        return html `
      <div class="row" @click=${this._onClick}>
        ${this._renderTypeIcon(this.operation.type)}
        <div class="body">
          <span class="name" title=${this._name()}>${this._name()}</span>
          ${this.subtitle
            ? html `<span class="subtitle" title=${this.subtitle}
                >${this.subtitle}</span
              >`
            : nothing}
        </div>
        ${this.hideTypeBadge
            ? nothing
            : html `<status-card
              .label=${meta.label}
              .variant=${meta.variant}
            ></status-card>`}
        ${showActions
            ? html `<div class="actions">
              ${showEdit
                ? html `<icon-button
                    variant="primary"
                    outlined
                    size="small"
                    aria-label=${msg('Edit operation', { id: 'scenarioOperation.action.edit.aria', desc: 'Accessible label for the edit button on a scenario operation row' })}
                    icon-name="edit"
                    @button-click=${this._onEdit}
                  ></icon-button>`
                : nothing}
              ${this.hideDelete
                ? nothing
                : html `<icon-button
                    variant="danger"
                    outlined
                    size="small"
                    aria-label=${msg('Delete operation', { id: 'scenarioOperation.action.delete.aria', desc: 'Accessible label for the delete button on a scenario operation row' })}
                    icon-name="delete"
                    @button-click=${this._onDelete}
                  ></icon-button>`}
            </div>`
            : nothing}
      </div>
    `;
    }
};
ScenarioOperation.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    /* Card-style row mirroring the SCN1 .op-item: white bg, 1px border,
       small type icon on the left, name + address stacked, hover reveals
       the action buttons anchored top-right. */
    .row {
      position: relative;
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 12px;
      background: #fff;
      border: 1px solid #e6ebf2;
      border-radius: 8px;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
      box-sizing: border-box;
    }
    .row:hover {
      border-color: #b8c4d3;
    }
    :host([selected]) .row {
      border-color: #0078d4;
      box-shadow: 0 0 0 2px rgba(0, 120, 212, 0.15);
    }

    .type-icon {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      margin-top: 1px;
    }
    .type-icon svg {
      width: 11px;
      height: 11px;
    }

    .body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .name {
      font-size: 13px;
      font-weight: 500;
      color: #1a2433;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .subtitle {
      font-size: 11.5px;
      color: #7a8594;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    status-card {
      flex: 0 0 auto;
    }

    /* Hover-revealed action group anchored top-right, mirroring
       .op-item-actions in the mockup. */
    .actions {
      position: absolute;
      top: 8px;
      right: 8px;
      display: flex;
      gap: 4px;
      opacity: 0;
      transition: opacity 0.15s;
      pointer-events: none;
    }
    .row:hover .actions {
      opacity: 1;
      pointer-events: auto;
    }
    icon-button {
      flex: 0 0 auto;
    }
  `;
__decorate([
    property({ type: Object })
], ScenarioOperation.prototype, "operation", void 0);
__decorate([
    property({ type: String, attribute: 'poi-name' })
], ScenarioOperation.prototype, "poiName", void 0);
__decorate([
    property({ type: String })
], ScenarioOperation.prototype, "subtitle", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ScenarioOperation.prototype, "selected", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-delete' })
], ScenarioOperation.prototype, "hideDelete", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-edit' })
], ScenarioOperation.prototype, "hideEdit", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-type-badge' })
], ScenarioOperation.prototype, "hideTypeBadge", void 0);
ScenarioOperation = __decorate([
    localized(),
    customElement('scenario-operation')
], ScenarioOperation);
export { ScenarioOperation };
//# sourceMappingURL=ScenarioOperation.js.map